"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";

// Real demos exported from Screenlet. Drop a new .mp4 in /public/examples
// and append an entry here — the carousel picks it up.
const EXAMPLES = [
	{
		title: "Sheets",
		blurb: "A spreadsheet tutorial with cinematic zoom.",
		video: "/examples/sheet2.mp4",
	},
	{
		title: "ChromePilot",
		blurb: "A product landing-page demo in an iPhone 17 Pro Max mockup.",
		video: "/examples/chromepilot-landing-page.mp4",
	},
	{
		title: "Adil Ahmed — Portfolio",
		blurb: "A personal portfolio site walkthrough.",
		video: "/examples/adil-ahmed-portfolio.mp4",
	},
	{
		title: "Duolingo",
		blurb: "A language-learning app demo in an iPad Pro 11 (M4) mockup.",
		video: "/examples/duolingo-demo.mp4",
	},
	{
		title: "Curvingo",
		blurb: "A planarity puzzle demo on the Curvingo platform.",
		video: "/examples/curvingo-puzzle.mp4",
	},

];

export function ExamplesCarousel() {
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{ align: "start", dragFree: true, containScroll: "trimSnaps" },
		[
			// Lets a two-finger trackpad / horizontal wheel swipe drive the carousel.
			WheelGesturesPlugin(),
		],
	);

	// Indices of slides currently within the viewport. Only these load & play
	// their video, so off-screen demos never get downloaded.
	const [inView, setInView] = useState<number[]>([]);

	const onSlidesInView = useCallback(() => {
		if (emblaApi) setInView(emblaApi.slidesInView());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSlidesInView();
		emblaApi.on("slidesInView", onSlidesInView);
		return () => {
			emblaApi.off("slidesInView", onSlidesInView);
		};
	}, [emblaApi, onSlidesInView]);

	return (
		<div className="overflow-hidden" ref={emblaRef}>
			<div className="flex touch-pan-y">
				{EXAMPLES.map((ex, i) => (
					<ExampleSlide key={ex.video} example={ex} visible={inView.includes(i)} />
				))}
			</div>
		</div>
	);
}

function ExampleSlide({
	example,
	visible,
}: {
	example: (typeof EXAMPLES)[number];
	visible: boolean;
}) {
	const videoRef = useRef<HTMLVideoElement>(null);
	// Latches true the first time the slide scrolls into view, so we mount the
	// <video> (and start the download) only on demand — but keep it once loaded.
	const [load, setLoad] = useState(false);

	useEffect(() => {
		if (visible) setLoad(true);
	}, [visible]);

	// Play only while in view; pause off-screen to spare CPU/decoding.
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		if (visible) {
			video.play().catch(() => {});
		} else {
			video.pause();
		}
	}, [visible, load]);

	return (
		<div className="min-w-0 shrink-0 grow-0 basis-[88%] pl-4 first:pl-6 last:pr-6 sm:basis-[58%] lg:basis-[44%]">
			<div className="border-border bg-card overflow-hidden rounded-2xl border shadow-sm transition-shadow hover:shadow-lg">
				<div className="bg-muted aspect-video overflow-hidden">
					{load ? (
						<video
							ref={videoRef}
							src={example.video}
							className="pointer-events-none h-full w-full bg-black object-cover"
							loop
							muted
							playsInline
							preload="auto"
							draggable={false}
							aria-label={`${example.title} demo`}
						/>
					) : (
						// Lightweight placeholder — no network cost until scrolled to.
						<div className="from-muted to-card h-full w-full animate-pulse bg-linear-to-br" />
					)}
				</div>
				<div className="p-4">
					<h3 className="text-base font-semibold">{example.title}</h3>
					<p className="text-muted-foreground mt-1 text-sm">{example.blurb}</p>
				</div>
			</div>
		</div>
	);
}
